import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { Unsubscribe } from 'router5/dist/types/base';
import { IAccountDetailsDTO, IAccountsDAO } from '../../api/accounts.dao';
import { IRouter } from '../../infrastructure/router';
import { RouteParams, Routes } from '../../routes-table';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { PagingViewModel } from '../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { MINIMAL_SEARCH_LENGTH } from '../../constants/common.constants';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../infrastructure/sub-windows';
import { AccountForm } from '../_accounts/_account-form/account-form';

class AccountsVM implements IModuleVM {
    @mobx.observable
    private accountList: IAccountDetailsDTO[];
    private subscriptions: Unsubscribe[];

    @mobx.observable
    public targetAccountId: number;
    @mobx.observable
    private errorMessage: string;
    @mobx.observable
    private name: string;
    @mobx.observable
    public isLoading: boolean;
    @mobx.observable
    private reservedSubdomains: string[];
    @mobx.observable
    public searchQuery: string;
    @mobx.observable
    private accountIdToEditPopupMap: { [key: string]: ISubWindowFacade };

    public paginationVM: PagingViewModel;

    constructor(
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
    ) {
        this.paginationVM = new PagingViewModel();
        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchAccountsList);
        this.accountIdToEditPopupMap = {};
    }

    public getPagingVM() {
        return this.paginationVM;
    }

    public getAccountId(): number {
        return this.targetAccountId;
    }

    public getErrorMessage(): string {
        return this.errorMessage;
    }

    public getName(): string {
        return this.name;
    }

    public getAccountsList(): IAccountDetailsDTO[] {
        return this.accountList;
    }

    public getReservedSubdomains(): string[] {
        return this.reservedSubdomains;
    }

    @mobx.action
    public setName(name: string) {
        this.name = name;
    }

    didMount(): void {
        this.subscriptions = [];

        this.subscriptions.push(this.router.listenAll([Routes.AccountsList, Routes.Default], async () => {
            this.fetchAccountsList();
            mobx.runInAction(() => {
                this.targetAccountId = null;
            });
        }));
    }

    willUnmount(): void {
        this.subscriptions.forEach(x => x());
    }

    @mobx.action
    public fetchAccountsList = async () => {
        this.isLoading = true;
        try {
            const response = await this.accountsDAO.getUserAccounts({
                page: this.paginationVM.getCurrentPageForBE(),
                size: this.paginationVM.getPageSize(),
                query: this.searchQuery ? this.searchQuery.trim() : ''
            });
            this.onFetchAccountListSuccess(response);
        } catch (error) {
            this.onFetchAccountListError(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    public async fetchReservedSubdomains(): Promise<void> {
        this.isLoading = true;
        try {
            const response = await this.accountsDAO.getReservedSubdomains();
            this.onFetchReservedSubdomainsSuccess(response);
        } catch (error) {
            this.onFetchReservedSubdomainsError(error);
        }
        finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    @mobx.action
    private onFetchAccountListSuccess(response): void {
        this.accountList = response.content;
        this.paginationVM.setTotalRecords(response.totalElements);
    }

    @mobx.action
    private onFetchReservedSubdomainsSuccess(response): void {
        this.reservedSubdomains = response;
    }

    @mobx.action
    public searchUsers = (query: string): void => {
        this.searchQuery = query;

        if (query.trim().length >= MINIMAL_SEARCH_LENGTH) {
            this.paginationVM.selectPage(1);
            this.fetchAccountsList();
        }
    };

    public getSearchQuery(): string {
        return this.searchQuery;
    }

    @mobx.action
    public resetSearchQuery = (): void => {
        this.searchQuery = '';
        this.paginationVM.selectPage(1);
        this.fetchAccountsList();
    };

    private onFetchAccountListError(error): void {
        this.errorMessage = this.errorParsingService.extractMessage(error);
    }
    private onFetchReservedSubdomainsError(error): void {
        this.errorMessage = this.errorParsingService.extractMessage(error);
    }

    @mobx.action
    public editAccount = (accountId: number, intl: IntlShape) => {
        let shallShowNewEditWindow = true;

        const alreadyShownSubWindow = this.accountIdToEditPopupMap[accountId];

        if (alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed) {
            alreadyShownSubWindow.maximize();
            shallShowNewEditWindow = false;
        }

        if (shallShowNewEditWindow) {
            this.accountIdToEditPopupMap[accountId] = this.showEditAccountForm(accountId, intl);
        }
    };

    public showCreateAccountForm = (intl: IntlShape) => {
        return this.subWindowService.showSubWindow({
            componentToHost: AccountForm,
            componentToHostProps: {
                accountId: this.targetAccountId,
                reservedSubdomains: [],
                fetchAccountsList: this.fetchAccountsList,
            },
            title: intl.formatMessage(
                {
                    id: `oe.accounts.newAccount`,
                    description: 'New Account button',
                    defaultMessage: '{prefix}New Account',
                },
                {
                    prefix: ''
                }
            )
        });
    };

    private closeEditAccountForm = (id: string) => {
        this.accountIdToEditPopupMap[id].close();
    };

    public showEditAccountForm = (accountId: number, intl: IntlShape) => {
        return this.subWindowService.showSubWindow({
            componentToHost: AccountForm,
            componentToHostProps: {
                accountId: accountId,
                reservedSubdomains: [],
                fetchAccountsList: this.fetchAccountsList,
                closeEditAccountForm: this.closeEditAccountForm,
            },
            title: intl.formatMessage({
                id: `oe.accounts.editAccount`,
                defaultMessage: 'Edit Account',
                description: 'Button label in editing account popover'
            })
        });
    };

    public isAccountEditFormShown = (id: string) => {
        return this.accountIdToEditPopupMap[id]
            && this.accountIdToEditPopupMap[id].getStatus() !== SubWindowStatus.Closed;
    };

}

export { AccountsVM };
